import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';

import { filter, map, startWith } from 'rxjs/operators';

import { AppIconComponent } from '@components/@fec/app-icon.component';
import { ButtonComponent } from '@components/@fec/button.component';

import { MfaEnrollmentStateService } from '@services/helper/mfa-enrollment-state.service';
import { TokenService } from '@services/helper/token.service';
import { UserPermissionService } from '@services/helper/user-permission.service';
import { UserProfileStateService } from '@services/helper/user-profile-state.service';

import { environment } from '@environments/environment';

/**
 * Global overlay prompting users without MFA to enroll. Hidden on the MFA settings route.
 */
@Component({
  selector: 'app-mfa-required-overlay',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent, AppIconComponent],
  templateUrl: './mfa-required-overlay.component.html',
})
export class MfaRequiredOverlayComponent {
  private readonly router = inject(Router);
  private readonly tokenService = inject(TokenService);
  private readonly userPermissions = inject(UserPermissionService);
  private readonly profileState = inject(UserProfileStateService);
  private readonly mfaEnrollmentState = inject(MfaEnrollmentStateService);

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map(() => this.router.url.split('?')[0]),
      startWith(this.router.url.split('?')[0]),
    ),
    { initialValue: this.router.url.split('?')[0] },
  );

  protected readonly mfaGraceDays = computed((): number | null => {
    const fromProfile = this.profileState.profileData()?.mfa_grace_days_remaining;
    if (typeof fromProfile === 'number' && !Number.isNaN(fromProfile)) return fromProfile;
    const fromCached = this.userPermissions.user()?.mfa_grace_days_remaining;
    if (typeof fromCached === 'number' && !Number.isNaN(fromCached)) return fromCached;
    return null;
  });

  /** sessionStorage key — the reminder stays dismissed for the rest of the browser session. */
  private static readonly DISMISS_KEY = 'mfa-reminder-dismissed';

  /** Whether the user has dismissed the reminder this session (persists across reloads). */
  protected readonly dismissed = signal<boolean>(this.readDismissed());

  /** Hidden by default until `/me` is loaded and all show conditions are met. */
  protected readonly showOverlay = computed(() => {
    if (!environment.isMfaEnabled) return false;
    if (this.dismissed()) return false;
    if (!this.tokenService.token()) return false;
    if (!this.userPermissions.user()) return false;
    if (this.userPermissions.isMfaEnabled()) return false;
    const path = this.url();
    if (path.includes('/my-preferences/mfa')) return false;
    if (path.startsWith('/auth')) return false;
    return true;
  });

  /**
   * Navigates to MFA settings and flags auto-start of `/mfa/enable` on that page.
   */
  protected onAssignMfa(): void {
    this.mfaEnrollmentState.requestSetupStart();
    void this.router.navigate(['/my-preferences/mfa']);
  }

  /** Dismisses the reminder for this session — it won't re-appear until the next session. */
  protected onDismiss(): void {
    this.dismissed.set(true);
    try {
      sessionStorage.setItem(MfaRequiredOverlayComponent.DISMISS_KEY, '1');
    } catch {
      /* sessionStorage unavailable (private mode) — in-memory signal still hides it. */
    }
  }

  /** Reads the persisted session-dismiss flag (defensive — sessionStorage may be blocked). */
  private readDismissed(): boolean {
    try {
      return sessionStorage.getItem(MfaRequiredOverlayComponent.DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  }
}
